import { z } from 'zod';

/** Reusable primitives shared across DTOs. */

/** Money is always integer cents, EUR, non-negative. */
export const centsSchema = z
  .number()
  .int('Amount must be in integer cents')
  .nonnegative('Amount must be non-negative');

export const emailSchema = z.string().email().toLowerCase().trim();

export const cuidSchema = z.string().cuid();

/** Fiscal id (CIF / NIF / etc.) — loose validation (Holded is authoritative). */
export const cifVatSchema = z.string().trim().min(1).max(32);

/**
 * VAT number (companies only). Loose format: optional 2-letter country prefix + 2..14
 * alphanumerics; spaces/dots/dashes stripped, upper-cased. VIES (not this regex) is the
 * real check; we only reject obvious junk so a free-text VAT (incl. non-EU) is accepted.
 */
export const vatNumberSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s.\-]/g, '').toUpperCase())
  .refine((v) => /^[A-Z0-9]{4,16}$/.test(v), 'Invalid VAT number format');

export const addressSchema = z.object({
  line1: z.string().trim().min(1).max(200),
  line2: z.string().trim().max(200).optional(),
  city: z.string().trim().min(1).max(120),
  postalCode: z.string().trim().min(1).max(16),
  province: z.string().trim().max(120).optional(),
  countryCode: z.string().trim().length(2).toUpperCase(),
});
export type AddressDto = z.infer<typeof addressSchema>;

/**
 * A delivery address in a client's managed list: a postal address plus a stable `id`
 * (assigned server-side on create) and an optional human `label` (e.g. "Clinic").
 */
/** One phone format: country code + number ("+34 600 000 000"). */
export const phoneSchema = z
  .string()
  .trim()
  .regex(/^\+\d{1,3}[\d\s().-]{6,20}$/, 'Phone must be in international format (+34 …)');

export const deliveryAddressSchema = addressSchema.extend({
  id: z.string().trim().max(64).optional(),
  label: z.string().trim().max(80).optional(),
  /** The person the carrier asks for at this address. */
  contactName: z.string().trim().max(120).optional(),
  /** The phone the carrier calls for this delivery. */
  contactPhone: phoneSchema.optional(),
});
export type DeliveryAddressDto = z.infer<typeof deliveryAddressSchema>;

/** Pagination query shared by list endpoints. */
export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(20),
});
export type PaginationQueryDto = z.infer<typeof paginationQuerySchema>;
